import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../../redux/store';
import { openDillToast } from '../../../../../../../utils/helpers';
import {
  updateUser,
  getSupplierSummary,
} from '../../../../../../../redux/services/suppliersService';
import { getUserDetails, getUsers } from '../../../../../../../redux/services/authService';
import { DillModal, DillField, DillInput, DillButton } from '../../../../../../general/Dill';
import { AppUser, AppUserRole, getUserArBranches } from '@dill/dill-shared';
import {
  SupplierUserRolesSection,
  deriveSelectedRoles,
} from '../SupplierUserRolesSection/SupplierUserRolesSection';
import { SupplierUserBranchAccessSection } from '../SupplierUserBranchAccessSection/SupplierUserBranchAccessSection';
import { useSupplierBranchOptions } from '../../../../../../../utils/hooks/useSupplierBranchOptions';

function EditSupplierUserModal({
  open,
  handleClose,
  supplierId,
  user,
}: {
  open: boolean;
  handleClose: () => void;
  supplierId: string;
  user: AppUser;
}) {
  const dispatch = useAppDispatch();
  const authUserId = useAppSelector((state) => state.auth.user?.id);
  const { branchOptions, isLoadingBranches } = useSupplierBranchOptions();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [roles, setRoles] = useState<AppUserRole[]>([]);
  const [branches, setBranches] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && open) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setRoles((user.roles as AppUserRole[]) || []);
      setBranches(getUserArBranches({ user, supplierId }));
    }
  }, [user, open, supplierId]);

  const handleSave = async () => {
    if (!firstName || !lastName) {
      openDillToast({ type: 'ERROR', message: 'Please fill in all required fields' });
      return;
    }

    const { creditApp, ar } = deriveSelectedRoles(roles);
    if (creditApp === 'NO_ACCESS' && ar === 'NO_ACCESS') {
      openDillToast({ type: 'ERROR', message: 'Please select at least one role' });
      return;
    }

    setLoading(true);
    const result = await dispatch(
      updateUser({
        supplierId,
        userId: user.id,
        userDetails: {
          firstName,
          lastName,
          roles,
          // Keep assignments for the user's other suppliers intact — this map is keyed by supplier.
          arBranchesBySupplierId: {
            ...(user.arBranchesBySupplierId ?? {}),
            [supplierId]: branches,
          },
        },
      })
    );
    setLoading(false);

    if (result.type === 'supplier/updateUser/fulfilled') {
      openDillToast({ type: 'SUCCESS', message: 'User updated successfully' });
      // Editing your own branch access changes what you're allowed to see. `state.auth.user` is the
      // copy the UI filters against, and nothing else refreshes it, so without this the frontend
      // would keep offering customers the backend now rejects.
      if (authUserId === user.id) {
        dispatch(getUserDetails());
      }
      dispatch(getSupplierSummary({ supplierId }));
      dispatch(
        getUsers({
          data: {
            roles: ['CREDIT_APP_DECISION_MAKER', 'CREDIT_APP_REVIEWER'],
            linkedSupplierIds: [supplierId],
          },
        })
      );
      handleClose();
    } else {
      openDillToast({ type: 'ERROR', message: result.payload as string });
    }
  };

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Edit Supplier User"
      subtitle="Update user details and role assignments"
      maxWidth="540px"
      footer={
        <>
          <DillButton
            onClick={handleClose}
            label="Cancel"
            variant="secondary"
            isDisabled={loading}
          />
          <DillButton onClick={handleSave} label="Save Changes" isLoading={loading} />
        </>
      }>
      <div className="grid grid-cols-2 gap-3">
        <DillField label="First Name" required>
          <DillInput value={firstName} onChange={setFirstName} placeholder="Enter first name" />
        </DillField>
        <DillField label="Last Name" required>
          <DillInput value={lastName} onChange={setLastName} placeholder="Enter last name" />
        </DillField>
      </div>
      <DillField label="Email">
        <DillInput
          value={user.email || ''}
          onChange={() => {}}
          placeholder="Email address"
          type="email"
          disabled={true}
        />
      </DillField>
      <SupplierUserRolesSection roles={roles} onRolesChange={setRoles} />
      <SupplierUserBranchAccessSection
        branchOptions={branchOptions}
        isLoading={isLoadingBranches}
        branches={branches}
        onBranchesChange={setBranches}
      />
    </DillModal>
  );
}

export default EditSupplierUserModal;
